import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { GlassCard } from '../ui/GlassCard';
import './Auth.css';

/**
 * OAuthCallback
 *
 * Mounted at route /oauth-callback (public route).
 *
 * After a successful OAuth SSO login the backend redirects to:
 *   {FRONTEND_BASE_URL}/#/oauth-callback?token=<jwt>
 *
 * This component:
 * 1. Reads the `token` query param from the URL hash fragment.
 * 2. Stores it in localStorage under the same key the AuthContext uses.
 * 3. Navigates to /dashboard — AuthContext will pick up the token automatically.
 */
const OAuthCallback: React.FC = () => {
    const navigate = useNavigate();
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        // When using HashRouter, the fragment is everything after '#'.
        // The URL looks like: http://host/#/oauth-callback?token=xxx
        // window.location.hash = '#/oauth-callback?token=xxx'
        // React Router gives us query params via URLSearchParams on the search portion.
        // However, since we use BrowserRouter (not HashRouter), the URL from the backend
        // redirect is: http://host/#/oauth-callback?token=xxx
        // The hash '#/oauth-callback?token=xxx' needs to be parsed manually.
        const hash = window.location.hash; // e.g. '#/oauth-callback?token=xxxxx'
        const queryStart = hash.indexOf('?');
        let token: string | null = null;

        if (queryStart !== -1) {
            const searchParams = new URLSearchParams(hash.slice(queryStart));
            token = searchParams.get('token');
        }

        // Fallback: token may be in the normal query string (non-hash routing)
        if (!token) {
            const searchParams = new URLSearchParams(window.location.search);
            token = searchParams.get('token');
        }

        if (token) {
            localStorage.setItem('access_token', token);
            // Navigate to dashboard — the AuthContext will call /auth/me with the new token
            navigate('/dashboard', { replace: true });
        } else {
            setError('No authentication token found. Please try signing in again.');
        }
    }, [navigate]);

    return (
        <div className="auth-page">
            <GlassCard variant="strong" padding="lg" className="auth-container">
                <div className="auth-header">
                    <h1>AiMe</h1>
                    {error ? (
                        <div className="auth-error" style={{ marginTop: '1rem' }}>
                            {error}
                        </div>
                    ) : (
                        <h2>Signing you in&hellip;</h2>
                    )}
                </div>
            </GlassCard>
        </div>
    );
};

export default OAuthCallback;
